import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const roles = [
  { value: 'GERENCIA', label: 'Gerência' },
  { value: 'RECEPCAO_COMPRAS_AGENDA', label: 'Recepção - Compras e Agenda' },
  { value: 'RECEPCAO_PROJETOS', label: 'Recepção - Projetos' },
  { value: 'PRODUCAO', label: 'Produção' },
  { value: 'MONTAGEM_CLIENTE', label: 'Montagem no Cliente' },
];

const CeoCreateUser = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { register, handleSubmit, watch, formState: { errors, isSubmitting }, reset } = useForm({
    defaultValues: {
      full_name: '',
      email: '',
      role: 'GERENCIA',
      password: '',
      confirmPassword: '',
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (payload) => userService.createUser(payload),
    onSuccess: () => {
      toast.success('Funcionário criado com sucesso');
      queryClient.invalidateQueries(['users']);
      reset();
      navigate('/users');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Erro ao criar funcionário';
      toast.error(message);
    },
  });

  const onSubmit = (values) => {
    if (values.password !== values.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }

    const payload = {
      full_name: values.full_name,
      email: values.email,
      role: values.role,
      password: values.password,
      birth_date: values.birth_date,
      hire_date: values.hire_date,
    };

    createUserMutation.mutate(payload);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Novo Funcionário</h1>
        <p className="text-secondary-600">Apenas o CEO pode acessar esta página.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Dados do Funcionário</h2>
          <p className="text-sm text-secondary-600">Preencha os campos para cadastrar um novo funcionário.</p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Nome completo</label>
                <input
                  className="input"
                  placeholder="Ex: João da Silva"
                  {...register('full_name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo de 3 caracteres' } })}
                />
                {errors.full_name && <p className="text-sm text-danger-600 mt-1">{errors.full_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                <input
                  className="input"
                  type="email"
                  placeholder="email@empresa.com"
                  {...register('email', {
                    required: 'Email é obrigatório',
                    pattern: { value: /[^@\s]+@[^@\s]+\.[^@\s]+/, message: 'Email inválido' },
                  })}
                />
                {errors.email && <p className="text-sm text-danger-600 mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Função (Role)</label>
                <select className="input" {...register('role', { required: 'Role é obrigatório' })}>
                  {roles.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {errors.role && <p className="text-sm text-danger-600 mt-1">{errors.role.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Data de Nascimento</label>
                <input
                  type="date"
                  className="input"
                  {...register('birth_date', {
                    required: 'Data de nascimento é obrigatória',
                    validate: (value) => {
                      if (!value) return true;
                      const date = new Date(value);
                      const today = new Date();
                      const age = today.getFullYear() - date.getFullYear();
                      if (date > today) return 'Data não pode ser futura';
                      if (age > 120) return 'Idade inválida';
                      return true;
                    }
                  })}
                />
                {errors.birth_date && <p className="text-sm text-danger-600 mt-1">{errors.birth_date.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Data de Admissão</label>
                <input
                  type="date"
                  className="input"
                  {...register('hire_date', {
                    required: 'Data de admissão é obrigatória',
                    validate: (value) => {
                      if (!value) return true;
                      const date = new Date(value);
                      const today = new Date();
                      today.setHours(23, 59, 59, 999);
                      if (date > today) return 'Data não pode ser futura';
                      return true;
                    }
                  })}
                />
                {errors.hire_date && <p className="text-sm text-danger-600 mt-1">{errors.hire_date.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Senha</label>
                <input
                  className="input"
                  type="password"
                  placeholder="••••••"
                  {...register('password', { required: 'Senha é obrigatória', minLength: { value: 6, message: 'Mínimo de 6 caracteres' } })}
                />
                {errors.password && <p className="text-sm text-danger-600 mt-1">{errors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Confirmar Senha</label>
                <input
                  className="input"
                  type="password"
                  placeholder="••••••"
                  {...register('confirmPassword', { required: 'Confirmação obrigatória' })}
                />
                {errors.confirmPassword && <p className="text-sm text-danger-600 mt-1">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/users')}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting || createUserMutation.isLoading}
              >
                {createUserMutation.isLoading ? 'Salvando...' : 'Salvar Funcionário'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CeoCreateUser; 